<template>
  <div class="visitor-register">
    <div class="header">
      <van-icon
        v-if="showBack"
        name="arrow-left"
        class="back-icon"
        @click="onClickBack"
      />
      <h1 class="header-title">访客自助登记</h1>
      <p class="header-subtitle">请如实填写，提交后将通知被访人审批</p>
    </div>

    <div class="form-wrap">
      <!-- 基本信息 -->
      <div class="form-card">
        <h2 class="form-card-title">基本信息</h2>
        <div class="form-item">
          <label class="form-label"><span class="required">*</span>姓名</label>
          <input
            v-model="form.visitorName"
            class="form-input"
            type="text"
            placeholder="请输入您的姓名"
            maxlength="30"
          />
          <span v-if="form.visitorName.length" class="form-word-limit">{{ form.visitorName.length }}/30</span>
        </div>
        <div class="form-item">
          <label class="form-label"><span class="required">*</span>手机号</label>
          <input
            v-model="form.phone"
            class="form-input"
            type="tel"
            placeholder="请输入手机号"
            maxlength="16"
            inputmode="numeric"
          />
        </div>
        <div class="form-item">
          <label class="form-label"><span class="required">*</span>车牌号码</label>
          <input
            v-model="form.plateNo"
            class="form-input"
            type="text"
            placeholder="如:粤B12345"
            maxlength="16"
            autocapitalize="characters"
            @input="onPlateInput"
          />
          <span v-if="form.plateNo.length" class="form-word-limit">{{ form.plateNo.length }}/16</span>
        </div>
        <div class="form-item">
          <label class="form-label">身份证号</label>
          <input
            v-model="form.idCard"
            class="form-input"
            type="text"
            placeholder="选填"
            maxlength="30"
          />
        </div>
      </div>

      <!-- 来访信息 -->
      <div class="form-card">
        <h2 class="form-card-title">来访信息</h2>
        <div class="form-item">
          <label class="form-label"><span class="required">*</span>来访事由</label>
          <input
            v-model="form.reason"
            class="form-input"
            type="text"
            placeholder="如：商务洽谈、技术交流、面试等"
            maxlength="250"
          />
          <span v-if="form.reason.length" class="form-word-limit">{{ form.reason.length }}/250</span>
        </div>
        <div class="form-item">
          <label class="form-label"><span class="required">*</span>计划到访开始时间</label>
          <div
            class="form-input form-input-readonly"
            :class="{ placeholder: !form.visitStartText }"
            @click="showDateStart = true"
          >
            {{ form.visitStartText || '请选择' }}
            <van-icon name="arrow" class="form-arrow" />
          </div>
        </div>
        <div class="form-item">
          <label class="form-label"><span class="required">*</span>计划到访结束时间</label>
          <div
            class="form-input form-input-readonly"
            :class="{ placeholder: !form.visitEndText }"
            @click="showDateEnd = true"
          >
            {{ form.visitEndText || '请选择' }}
            <van-icon name="arrow" class="form-arrow" />
          </div>
        </div>
      </div>

      <!-- 被访人 -->
      <div class="form-card">
        <h2 class="form-card-title">被访人</h2>
        <div class="form-item">
          <label class="form-label"><span class="required">*</span>被访人部门</label>
          <div
            class="form-input form-input-readonly"
            :class="{ placeholder: !form.hostDeptText }"
            @click="onOpenDeptPicker"
          >
            {{ form.hostDeptText || '请选择部门' }}
            <van-icon name="arrow" class="form-arrow" />
          </div>
        </div>
        <div class="form-item">
          <label class="form-label"><span class="required">*</span>被访人姓名</label>
          <input
            v-model.trim="form.hostName"
            class="form-input"
            type="text"
            placeholder="请输入被访人姓名"
            maxlength="30"
            :disabled="!form.hostDeptId"
          />
        </div>
      </div>

      <van-button
        type="info"
        block
        class="submit-btn"
        :loading="submitting"
        loading-text="提交中…"
        @click="onSubmit"
      >
        提交申请
      </van-button>

      <p class="tip">
        提交后系统将通知被访人审批，审批通过后您将获得通行凭证，请留意短信或通知。
      </p>
    </div>

    <!-- 计划到访开始时间 -->
    <van-popup v-model="showDateStart" position="bottom">
      <van-datetime-picker
        v-model="form.visitStart"
        type="datetime"
        title="选择开始时间"
        :min-date="minDate"
        :formatter="dateTimeFormatter"
        @confirm="onConfirmVisitStart"
        @cancel="showDateStart = false"
      />
    </van-popup>
    <!-- 计划到访结束时间 -->
    <van-popup v-model="showDateEnd" position="bottom">
      <van-datetime-picker
        v-model="form.visitEnd"
        type="datetime"
        title="选择结束时间"
        :min-date="form.visitStart || minDate"
        :formatter="dateTimeFormatter"
        @confirm="onConfirmVisitEnd"
        @cancel="showDateEnd = false"
      />
    </van-popup>
    <!-- 部门选择：单列逐级 + 顶部路径条（可点击回退） -->
    <van-popup
      v-model="showPickerDept"
      position="bottom"
      class="dept-picker-popup"
      :lazy-render="false"
      get-container="body"
      @opened="onDeptPickerPopupOpened"
    >
      <div class="dept-picker-panel">
        <div class="dept-breadcrumb">
          <span
            class="dept-bc-item"
            :class="{ 'dept-bc-active': deptPickerStack.length === 0 }"
            @click="onDeptBreadcrumbRoot"
          >全部</span>
          <span
            v-for="(seg, idx) in deptPickerStack"
            :key="seg.id + '-' + idx"
            class="dept-bc-node"
          >
            <span class="dept-bc-sep">/</span>
            <span
              class="dept-bc-item"
              :class="{ 'dept-bc-active': idx === deptPickerStack.length - 1 }"
              @click="onDeptBreadcrumbToIndex(idx)"
            >{{ seg.name }}</span>
          </span>
        </div>
        <p class="dept-bc-hint">{{ deptPickerHint }}</p>
        <van-picker
          title="选择部门"
          show-toolbar
          :key="deptPickerRenderKey"
          :columns="deptColumns"
          @confirm="onConfirmDept"
          @cancel="onDeptPickerCancel"
        />
      </div>
    </van-popup>
  </div>
</template>

<script>
import * as common from '@/api/common'
import { IotService } from '@/api/iotService'

export default {
  name: 'VisitorRegister',
  data () {
    const now = new Date()
    const start = new Date(now.getTime() + 60 * 60 * 1000)
    const end = new Date(now.getTime() + 60 * 60 * 4 * 1000)
    start.setSeconds(0, 0)
    end.setSeconds(0, 0)
    return {
      sid: '',
      customerId: null,
      sceneAccessToken: '',

      form: {
        visitorName: '',
        phone: '',
        plateNo: '',
        idCard: '',
        reason: '',
        visitStart: start,
        visitEnd: end,
        visitStartText: '',
        visitEndText: '',
        hostDeptId: '',
        hostDeptText: '',
        hostName: ''
      },

      showDateStart: false,
      showDateEnd: false,
      showPickerDept: false,
      submitting: false,
      minDate: new Date(),

      showBack: false,

      deptList: [],
      deptPickerParentId: 0,
      /** 已选中的上级路径（不含当前列表），用于路径条与回退 */
      deptPickerStack: [],
      reservationApproveSetting: null,
      formDisabled: false,
      initializing: true
    }
  },
  computed: {
    deptChildrenList () {
      return (this.deptList || []).filter(d => d && d.status === 1 && d.pid === this.deptPickerParentId)
    },
    deptColumns () {
      return this.deptChildrenList.map(d => d.name)
    },
    deptHasChildrenById () {
      return (deptId) => {
        return (this.deptList || []).some(d => d && d.status === 1 && d.pid === deptId)
      }
    },
    deptPickerRenderKey () {
      const tail = (this.deptPickerStack || []).map(s => s.id).join('-')
      return `dept-${this.deptPickerParentId}-${tail}`
    },
    deptPickerHint () {
      if (!this.deptPickerStack.length) return '请选择一级部门；有子部门时需选到最末级'
      return `当前在「${this.deptPickerStack[this.deptPickerStack.length - 1].name}」下选择子部门`
    }
  },
  created () {
    this.sid = (this.$route && this.$route.query && this.$route.query.sid) ? String(this.$route.query.sid) : ''
    this.formatDateTimes()

    // 从应用内部跳转（如首页入口）时，history 一般会大于 1，此时显示返回按钮；
    // 直接扫码打开（首个页面）时 history.length 通常为 1，不显示返回。
    if (typeof window !== 'undefined' && window.history && window.history.length > 1) {
      this.showBack = true
    }

    this.init()
  },
  methods: {
    async init () {
      if (!this.sid) {
        const redirect = `/visitor/register?sid=${encodeURIComponent(this.sid)}`
        this.$router.replace({ path: '/visitor/login', query: { sid: this.sid, redirect: redirect } })
        return
      }

      try {
        const featureRes = await IotService.getVisitorFeatureBySid(this.sid)
        if (!IotService.isVisitorFeatureEnabled(featureRes)) {
          this.$router.replace({
            path: '/visitor/unavailable',
            query: { sid: this.sid, type: 'feature' }
          })
          return
        }
      } catch (e) {
        this.$toast('访客功能状态获取失败，请稍后重试')
        return
      }

      const now = Date.now()
      const tokenSess = sessionStorage.getItem('visitorSceneAccessToken')
      const expireSess = parseInt(sessionStorage.getItem('visitorSceneAccessToken_expireAt') || '0', 10)
      const tokenLocal = localStorage.getItem('visitorSceneAccessToken')
      const expireLocal = parseInt(localStorage.getItem('visitorSceneAccessToken_expireAt') || '0', 10)

      let token = ''
      let customerId = ''

      // 优先 sessionStorage
      if (tokenSess && expireSess && now < expireSess) {
        token = tokenSess
        customerId = sessionStorage.getItem('visitorSceneCustomerId') || localStorage.getItem('visitorSceneCustomerId') || ''
      } else {
        // session 过期，清理
        sessionStorage.removeItem('visitorSceneAccessToken')
        sessionStorage.removeItem('visitorSceneAccessToken_expireAt')
        sessionStorage.removeItem('visitorSceneCustomerId')
      }

      // 再尝试 localStorage
      if (!token && tokenLocal && expireLocal && now < expireLocal) {
        token = tokenLocal
        customerId = localStorage.getItem('visitorSceneCustomerId') || '' // local 有效时 customerId 应在 local
      } else if (!token) {
        localStorage.removeItem('visitorSceneAccessToken')
        localStorage.removeItem('visitorSceneAccessToken_expireAt')
        localStorage.removeItem('visitorSceneCustomerId')
      }

      this.customerId = customerId ? Number(customerId) : null
      this.sceneAccessToken = token

      // 未登录访客态：先跳到登录页
      if (!token || !this.customerId) {
        const redirect = `/visitor/register?sid=${encodeURIComponent(this.sid)}`
        this.$router.replace({ path: '/visitor/login', query: { sid: this.sid, redirect: redirect } })
        return
      }

      this.initializing = true
      this.fetchDeptList()
        .then(() => this.fetchReservationApproveSetting())
        .finally(() => {
          this.initializing = false
        })
    },

    async fetchDeptList () {
      const res = await IotService.getDeptListBySceneToken(this.sceneAccessToken, this.sid)

      if (res && res.code === common.resCode.OK) {
        this.deptList = res.list || []
      } else {
        this.deptList = []
        this.$toast(res && res.msg ? res.msg : '获取部门列表失败')
      }
    },

    async fetchReservationApproveSetting () {
      const res = await IotService.reservationApproveQueryBySceneToken(this.sceneAccessToken, this.sid)

      if (res && res.code === common.resCode.OK) {
        this.reservationApproveSetting = res.info || {}
        const reservationEnabled = this.reservationApproveSetting.reservation_enable

        if (reservationEnabled !== 1) {
          this.$router.replace({
            path: '/visitor/unavailable',
            query: { sid: this.sid, type: 'reservation' }
          })
        }
      } else {
        this.$toast(res && res.msg ? res.msg : '获取预约审批设置失败')
      }
    },

    dateTimeFormatter (type, value) {
      if (type === 'year') return `${value}年`
      if (type === 'month') return `${value}月`
      if (type === 'day') return `${value}日`
      if (type === 'hour') return `${value}时`
      if (type === 'minute') return `${value}分`
      return value
    },

    onClickBack () {
      if (typeof window !== 'undefined' && window.history && window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.replace('/index')
      }
    },

    formatDateTimes () {
      this.form.visitStartText = common.fmtDate(this.form.visitStart, 'yyyy-MM-dd hh:mm')
      this.form.visitEndText = common.fmtDate(this.form.visitEnd, 'yyyy-MM-dd hh:mm')
    },

    normalizeToMinute (dateValue) {
      const d = new Date(dateValue)
      d.setSeconds(0, 0)
      return d
    },

    onPlateInput (e) {
      const raw = e && e.target ? e.target.value : ''
      const upper = raw.toUpperCase()
      if (upper !== this.form.plateNo) {
        this.form.plateNo = upper
      }
    },

    // 车牌号必填：仅按长度校验为7或8位
    isValidPlateNo (plateNo) {
      const plate = (plateNo || '').trim().toUpperCase()
      if (!plate) return false
      return plate.length === 7 || plate.length === 8
    },

    // 身份证号宽松校验：15位全数字；或18位（前17位数字+末位数字或X），不校验校验位
    isValidIdCard (idCard) {
      const code = (idCard || '').trim().toUpperCase()
      if (!code) return true
      return /^(\d{15}|\d{17}[\dX])$/.test(code)
    },

    onConfirmVisitStart (value) {
      const normalizedStart = this.normalizeToMinute(value)
      this.form.visitStart = normalizedStart
      this.form.visitStartText = common.fmtDate(normalizedStart, 'yyyy-MM-dd hh:mm')
      this.showDateStart = false
      if (this.form.visitEnd && this.form.visitEnd <= normalizedStart) {
        const end = new Date(normalizedStart.getTime() + 60 * 60 * 3 * 1000)
        end.setSeconds(0, 0)
        this.form.visitEnd = end
        this.form.visitEndText = common.fmtDate(end, 'yyyy-MM-dd hh:mm')
      }
    },

    onConfirmVisitEnd (value) {
      const normalizedEnd = this.normalizeToMinute(value)
      this.form.visitEnd = normalizedEnd
      this.form.visitEndText = common.fmtDate(normalizedEnd, 'yyyy-MM-dd hh:mm')
      this.showDateEnd = false
    },

    onConfirmDept (value) {
      const dept = this.deptChildrenList.find(d => d.name === value)
      if (!dept) return

      const hasChildren = this.deptHasChildrenById(dept.id)
      const pathNames = [...this.deptPickerStack.map(s => s.name), dept.name]

      if (hasChildren) {
        this.deptPickerStack.push({ id: dept.id, name: dept.name })
        this.deptPickerParentId = dept.id
        this.form.hostDeptId = ''
        this.form.hostName = ''
      } else {
        this.deptPickerParentId = 0
        this.deptPickerStack = []
        this.form.hostDeptId = dept.id
        this.form.hostDeptText = pathNames.join('/')
        this.form.hostName = ''
        this.showPickerDept = false
      }
    },

    onDeptBreadcrumbRoot () {
      this.deptPickerStack = []
      this.deptPickerParentId = 0
      this.form.hostDeptId = ''
      this.form.hostName = ''
    },

    onDeptBreadcrumbToIndex (idx) {
      const nextStack = this.deptPickerStack.slice(0, idx + 1)
      this.deptPickerStack = nextStack
      this.deptPickerParentId = nextStack.length ? nextStack[nextStack.length - 1].id : 0
      this.form.hostDeptId = ''
      this.form.hostName = ''
    },

    onOpenDeptPicker () {
      if (this.formDisabled) return
      this.deptPickerParentId = 0
      this.deptPickerStack = []
      this.form.hostDeptId = ''
      this.form.hostDeptText = ''
      this.form.hostName = ''
      this.showPickerDept = true
    },

    onDeptPickerCancel () {
      this.showPickerDept = false
      this.deptPickerParentId = 0
      this.deptPickerStack = []
      // 未选择到叶子部门时，不应填充部门信息
      this.form.hostDeptId = ''
      this.form.hostDeptText = ''
      this.form.hostName = ''
    },

    /** 首次打开时 van-picker 懒渲染可能导致高度未撑满露底，打开后触发一次重排 */
    onDeptPickerPopupOpened () {
      this.$nextTick(() => {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('resize'))
        }
      })
    },

    normalizeFormFields () {
      this.form.visitorName = (this.form.visitorName || '').trim()
      this.form.phone = (this.form.phone || '').trim()
      this.form.plateNo = (this.form.plateNo || '').trim().toUpperCase()
      this.form.idCard = (this.form.idCard || '').trim().toUpperCase()
      this.form.reason = (this.form.reason || '').trim()
      this.form.hostName = (this.form.hostName || '').trim()
    },

    validate () {
      if (this.formDisabled) {
        this.$toast('当前无法登记')
        return false
      }
      if (!this.form.visitorName || !this.form.visitorName.trim()) {
        this.$toast('请输入姓名')
        return false
      }
      if (!this.form.phone || !this.form.phone.trim()) {
        this.$toast('请输入手机号')
        return false
      }
      const phoneReg = /^1\d{10}$/
      if (!phoneReg.test(this.form.phone)) {
        this.$toast('请输入正确的手机号')
        return false
      }
      if (!this.form.plateNo || !this.form.plateNo.trim()) {
        this.$toast('请输入车牌号码')
        return false
      }
      if (!this.isValidPlateNo(this.form.plateNo)) {
        this.$toast('请输入正确的车牌号码')
        return false
      }
      if (this.form.idCard && this.form.idCard.trim() && !this.isValidIdCard(this.form.idCard)) {
        this.$toast('请输入正确的身份证号')
        return false
      }
      if (!this.form.reason || !this.form.reason.trim()) {
        this.$toast('请输入来访事由')
        return false
      }
      if (!this.form.visitStart || !this.form.visitEnd) {
        this.$toast('请选择到访时间')
        return false
      }
      if (this.form.visitEnd <= this.form.visitStart) {
        this.$toast('结束时间须晚于开始时间')
        return false
      }
      if (!this.form.hostDeptId) {
        this.$toast('请选择被访人部门')
        return false
      }
      if (!this.form.hostName) {
        this.$toast('请输入被访人姓名')
        return false
      }
      return true
    },

    waitMs (ms) {
      return new Promise(resolve => {
        setTimeout(resolve, ms)
      })
    },

    async onSubmit () {
      this.normalizeFormFields()
      if (!this.validate()) return
      if (this.submitting) return

      this.submitting = true
      try {
        const payload = this.getSubmitData()
        let res = await IotService.reservationAddBySceneToken(this.sceneAccessToken, this.sid, payload, true)

        if (res && res.code === common.resCode.OK && res.need_wait_try === true) {
          await this.waitMs(3000)
          res = await IotService.reservationAddBySceneToken(this.sceneAccessToken, this.sid, payload, false)
        }

        if (res && res.code === common.resCode.OK) {
          const reservationNo = res.reservation_no
          const auditStatus = res.audit_status

          this.resetForm()
          this.$router.replace({
            path: '/visitor/result',
            query: {
              sid: this.sid,
              reservation_no: reservationNo ? String(reservationNo) : '',
              audit_status: auditStatus !== undefined && auditStatus !== null ? String(auditStatus) : ''
            }
          })
        } else {
          this.$toast(res && res.msg ? res.msg : '提交失败，请重试')
        }
      } catch (e) {
        this.$toast(e && e.message ? e.message : '提交失败，请重试')
      } finally {
        this.submitting = false
      }
    },

    getSubmitData () {
      const submitStart = this.normalizeToMinute(this.form.visitStart)
      const submitEnd = this.normalizeToMinute(this.form.visitEnd)
      return {
        full_name: this.form.visitorName.trim(),
        phone_number: this.form.phone.trim(),
        plate_number: this.form.plateNo.trim().toUpperCase(),
        id_card: (this.form.idCard || '').trim().toUpperCase() || undefined,
        reason: this.form.reason.trim(),
        interviewee_department_id: this.form.hostDeptId,
        interviewee_full_name: this.form.hostName,
        reservation_start: common.fmtDate(submitStart, 'yyyy-MM-dd hh:mm:ss'),
        reservation_end: common.fmtDate(submitEnd, 'yyyy-MM-dd hh:mm:ss')
      }
    },

    resetForm () {
      const now = new Date()
      const start = new Date(now.getTime() + 60 * 60 * 1000)
      const end = new Date(now.getTime() + 60 * 60 * 4 * 1000)
      start.setSeconds(0, 0)
      end.setSeconds(0, 0)

      this.form = {
        visitorName: '',
        phone: '',
        plateNo: '',
        idCard: '',
        reason: '',
        visitStart: start,
        visitEnd: end,
        visitStartText: common.fmtDate(start, 'yyyy-MM-dd hh:mm'),
        visitEndText: common.fmtDate(end, 'yyyy-MM-dd hh:mm'),
        hostDeptId: '',
        hostDeptText: '',
        hostName: ''
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.visitor-register {
  min-height: 100vh;
  background: #f5f7fa;
  padding-bottom: calc(40px + env(safe-area-inset-bottom, 0));
}

.header {
  background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
  color: #fff;
  padding: 16px 20px;
  padding-top: calc(16px + env(safe-area-inset-top, 0));
  text-align: center;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.25);
  position: relative;
}

.header-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.header-subtitle {
  font-size: 13px;
  opacity: 0.95;
  margin: 4px 0 0 0;
}

.back-icon {
  position: absolute;
  left: 12px;
  top: calc(16px + env(safe-area-inset-top, 0));
  font-size: 20px;
  color: #fff;
}

.form-wrap {
  padding: 16px;
  max-width: 480px;
  margin: 0 auto;
}

.form-card {
  margin-bottom: 16px;
  padding: 20px 16px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.form-card-title {
  margin: 0 0 16px 0;
  padding-bottom: 12px;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  border-bottom: 1px solid #ebeef5;
  text-align: left;
}

.form-item {
  margin-bottom: 16px;
  position: relative;

  &:last-child {
    margin-bottom: 0;
  }
}

.form-label {
  display: block;
  font-size: 14px;
  color: #606266;
  margin-bottom: 8px;
  text-align: left;

  .required {
    color: #f56c6c;
    margin-right: 2px;
  }
}

.form-input {
  display: block;
  width: 100%;
  height: 48px;
  padding: 0 14px;
  font-size: 16px;
  color: #303133;
  background: #f5f7fa;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;

  &::placeholder {
    color: #c0c4cc;
  }

  &:focus {
    outline: none;
    border-color: #409eff;
    box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
  }
}

.form-input-readonly {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;

  &.placeholder {
    color: #c0c4cc;
  }

  &.disabled {
    color: #c0c4cc;
    cursor: not-allowed;
  }

  .form-arrow {
    color: #969799;
    font-size: 16px;
  }
}

.form-word-limit {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
  text-align: right;
}

.submit-btn {
  margin-top: 8px;
  height: 50px;
  font-size: 17px;
  border-radius: 10px;
  background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
  border: none;
}

.tip {
  font-size: 12px;
  color: #909399;
  margin-top: 16px;
  padding: 0 4px;
  line-height: 1.6;
  text-align: left;
}

.dept-picker-panel {
  background: #fff;
  /* 补全底部安全区，避免首帧露底看到下层页面（微信/WebView 常见） */
  padding-bottom: calc(8px + constant(safe-area-inset-bottom));
  padding-bottom: calc(8px + env(safe-area-inset-bottom, 0px));
}

.dept-breadcrumb {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  padding: 10px 16px 6px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-bottom: 1px solid #ebedf0;
  font-size: 13px;
  line-height: 1.4;
  white-space: nowrap;
}

.dept-bc-node {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.dept-bc-sep {
  color: #c8c9cc;
  padding: 0 4px;
  flex-shrink: 0;
}

.dept-bc-item {
  color: #646566;
  flex-shrink: 0;
}

.dept-bc-active {
  color: #1989fa;
  font-weight: 600;
}

.dept-bc-hint {
  margin: 0;
  padding: 6px 16px 8px;
  font-size: 12px;
  color: #969799;
  line-height: 1.5;
  background: #f7f8fa;
  border-bottom: 1px solid #ebedf0;
}
</style>
